import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, FileText, TrendingUp, CheckCircle2 } from 'lucide-react';
import { esgServices, esgBenefits } from '../data/mockData';

const ESGSupport = () => {
  return (
    <div className="page">
      {/* Hero Section */}
      <section className="page-hero hero-with-image">
        <div className="hero-background-image">
          <img 
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzl8MHwxfHNlYXJjaHw1fHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3NjMyMTg1MDF8MA&ixlib=rb-4.1.0&q=85"
            alt="ESG data visualization"
            loading="eager"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="page-hero-content">
            <div className="badge">ESG Compliance</div>
            <h1 className="heading-1">ESG Reporting & Improvement Services</h1>
            <p className="body-large">
              Practical ESG support for UK businesses. Navigate compliance, measure your impact, and demonstrate sustainability leadership to stakeholders.
            </p>
            <Link to="/contact" className="btn-primary">
              Start Your ESG Journey
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Partner Services */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Expert ESG Support</h2>
            <p className="section-subtitle">
              Our trusted ESG partner brings a decade of experience to help your business navigate compliance and unlock opportunities.
            </p>
          </div>

          <div className="esg-services-grid">
            <div className="esg-service-card">
              <div className="service-icon">
                <TrendingUp size={32} />
              </div>
              <h3 className="heading-3">Carbon Reporting & Disclosure</h3>
              <p className="body-medium">
                Full coverage of carbon disclosure including SECR, PPN, and ESOS. Measure Scope 1, 2, and 3 emissions with expert guidance.
              </p>
            </div>

            <div className="esg-service-card">
              <div className="service-icon">
                <Target size={32} />
              </div>
              <h3 className="heading-3">EcoVadis Certification</h3>
              <p className="body-medium">
                Secure first-time certification with confidence. Navigate the assessment process and improve your sustainability ratings.
              </p>
            </div>

            <div className="esg-service-card">
              <div className="service-icon">
                <FileText size={32} />
              </div>
              <h3 className="heading-3">ESG Consultancy</h3>
              <p className="body-medium">
                Flexible service models from consultancy-only to fractional ESG staff. Embed ESG into your business strategy for long-term value.
              </p>
            </div>

            <div className="esg-service-card">
              <div className="service-icon">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="heading-3">Global Frameworks</h3>
              <p className="body-medium">
                Expert support for GRI, ISSB, CSRD, B Corp, and other recognized ESG frameworks. Stay ahead of regulatory requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Why This Matters</h2>
          </div>

          <div className="benefits-list">
            <div className="benefit-item">
              <CheckCircle2 size={24} />
              <p className="body-medium">Fulfil increasing regulatory and buyer expectations around ESG disclosure</p>
            </div>
            <div className="benefit-item">
              <CheckCircle2 size={24} />
              <p className="body-medium">Gain access to tenders and contracts that demand strong sustainability credentials</p>
            </div>
            <div className="benefit-item">
              <CheckCircle2 size={24} />
              <p className="body-medium">Build resilience and long-term value by integrating ESG into operations</p>
            </div>
            <div className="benefit-item">
              <CheckCircle2 size={24} />
              <p className="body-medium">Receive expert support rather than going it alone — saving time, cost, and stress</p>
            </div>
            <div className="benefit-item">
              <CheckCircle2 size={24} />
              <p className="body-medium">Become "safe to do business with" — strengthening reputation and managing risk</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Models */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Flexible Service Models</h2>
            <p className="section-subtitle">
              Choose the support level that fits your needs.
            </p>
          </div>

          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">01</div>
              <h3 className="heading-3">Consultancy</h3>
              <p className="body-medium">
                Expert guidance on ESG strategy, carbon disclosure, and framework compliance. Project-based support when you need it.
              </p>
            </div>

            <div className="process-step">
              <div className="step-number">02</div>
              <h3 className="heading-3">ESG Software Platform</h3>
              <p className="body-medium">
                Subscription access to professional ESG management tools. Track, measure, and report your sustainability performance.
              </p>
            </div>

            <div className="process-step">
              <div className="step-number">03</div>
              <h3 className="heading-3">Fractional ESG Staff</h3>
              <p className="body-medium">
                Dedicated sustainability professionals working as part of your team. Ongoing support without full-time overhead.
              </p>
            </div>

            <div className="process-step">
              <div className="step-number">04</div>
              <h3 className="heading-3">Combined Approach</h3>
              <p className="body-medium">
                Mix consultancy, software, and staff support to create the perfect solution for your business. Fully customizable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Learn more about our ESG services</h2>
            <p className="section-subtitle">
              Watch how our trusted ESG partner helps businesses achieve sustainability excellence.
            </p>
          </div>
          <div style={{ maxWidth: '900px', margin: '0 auto', aspectRatio: '16/9' }}>
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/YPTWG0MZqAA"
              title="ESG Pro Services"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ borderRadius: '8px' }}
            ></iframe>
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Unrivalled Expertise</h2>
            <p className="section-subtitle">
              Founded in 2016, our trusted ESG partner brings a decade of experience with senior sustainability lawyers and certified practitioners.
            </p>
          </div>

          <div className="frameworks-grid">
            <div className="framework-item">
              <h3 className="heading-3">SECR</h3>
              <p className="body-small">Streamlined Energy & Carbon Reporting compliance</p>
            </div>
            <div className="framework-item">
              <h3 className="heading-3">PPN & ESOS</h3>
              <p className="body-small">Public Procurement and Energy Savings Opportunity Scheme</p>
            </div>
            <div className="framework-item">
              <h3 className="heading-3">CSRD</h3>
              <p className="body-small">Corporate Sustainability Reporting Directive</p>
            </div>
            <div className="framework-item">
              <h3 className="heading-3">GRI & ISSB</h3>
              <p className="body-small">Global Reporting Initiative and International Sustainability Standards</p>
            </div>
            <div className="framework-item">
              <h3 className="heading-3">EcoVadis</h3>
              <p className="body-small">Business sustainability ratings and certification</p>
            </div>
            <div className="framework-item">
              <h3 className="heading-3">B Corp</h3>
              <p className="body-small">Certified B Corporation standards and assessment</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-2">Ready to strengthen your ESG performance?</h2>
            <p className="body-large">
              Let's discuss your sustainability goals and compliance requirements.
            </p>
            <Link to="/contact" className="btn-primary">
              Get Started
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ESGSupport;
